import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { PaletteMode } from '@mui/material'

export type ThemePreference = 'light' | 'dark' | 'system'

interface ThemeModeContextValue {
  themeMode: ThemePreference
  resolvedMode: PaletteMode
  setThemeMode: (mode: ThemePreference) => void
  isDarkMode: boolean
  isLightMode: boolean
  isSystemMode: boolean
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined)

const STORAGE_KEY = 'tradelytic.theme-mode'

const getSystemMode = (): PaletteMode =>
  window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemePreference>(() => {
    if (typeof window === 'undefined') {
      return 'light'
    }

    const stored = window.localStorage.getItem(STORAGE_KEY) as ThemePreference | null
    return stored ?? 'system'
  })

  const [systemMode, setSystemMode] = useState<PaletteMode>(() => {
    if (typeof window === 'undefined') {
      return 'light'
    }

    return getSystemMode()
  })

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (event: MediaQueryListEvent) => {
      setSystemMode(event.matches ? 'dark' : 'light')
    }

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
    } else {
      // Safari < 14
      mediaQuery.addListener(handleChange)
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange)
      } else {
        mediaQuery.removeListener(handleChange)
      }
    }
  }, [])

  const setThemeMode = useCallback((mode: ThemePreference) => {
    setThemeModeState(mode)

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, mode)
    }
  }, [])

  const resolvedMode: PaletteMode = themeMode === 'system' ? systemMode : themeMode
  const isDarkMode = systemMode === 'dark'
  const isLightMode = systemMode === 'light'
  const isSystemMode = themeMode === 'system'

  const value = useMemo(
    () => ({
      themeMode,
      resolvedMode,
      setThemeMode,
      isDarkMode,
      isLightMode,
      isSystemMode,
    }),
    [resolvedMode, isDarkMode, themeMode, isSystemMode, setThemeMode, themeMode],
  )

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>
}

export const useThemeMode = (): ThemeModeContextValue => {
  const context = useContext(ThemeModeContext)

  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider')
  }

  return context
}
